import React, { FC, useRef, useState } from 'react'

import styled from '@emotion/styled'

import { ConfirmModal, ICONS_NAME, Mask, Scroller } from '@/elements'

import { ModalHeader } from './components'
import { useMediaQuery, useToggle } from '@hooks'
import { Color, device } from '@/utils'

const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  margin: 30px;
  max-height: calc(100% - 60px);
  height: ${({ $isMobile, $isMaximized }) =>
    ($isMobile || $isMaximized) && 'calc(100% - 60px)'};
  width: ${({ $isMobile, width, $isMaximized }) =>
    $isMobile || $isMaximized ? 'calc(100% - 60px)' : width};
  max-width: calc(100% - 60px);
  border-radius: 5px;
  background: ${Color.white};
  box-shadow:
    0 4px 4px rgba(32, 32, 32, 0.1),
    0 10px 25px rgba(118, 118, 118, 0.3);
  overflow: hidden;
`

const Modal = ({
  title,
  toggle,
  width = '730px',
  enableMaximize = true,
  maximized = false,
  setMaximized,
  confirmOnExit = false,
  children,
  closeOnClickOutside = true,
}) => {
    const [innerMaximized, setInnerMaximized] = useState(maximized)
    const [mouseDownTarget, setMouseDownTarget] = useState(null)
    const [showConfirmModal, toggleConfirmModal] = useToggle(false)
    const isMobile = useMediaQuery(device.md)
    const ref = useRef(null)

    const handleClose = () => {
        if (confirmOnExit) {
            toggleConfirmModal()
        } else {
            toggle()
        }
    }

    const toggleMaximize = () => {
        if (setMaximized) {
            setInnerMaximized(!maximized)
            setMaximized(!maximized)
        } else {
            setInnerMaximized(!innerMaximized)
        }
    }

    const handleMaskMouseUp = (e) => {
        if (mouseDownTarget === e.currentTarget) {
            handleClose()
        }

        setMouseDownTarget(null)
    }

    const handleMaskMouseDown = (e) => {
        if (e.target !== e.currentTarget) {
            return
        }

        if (e.detail > 1) {
            e.preventDefault()
        }

        if (closeOnClickOutside) {
            setMouseDownTarget(e.target)
        }
    }

    const closeProps = closeOnClickOutside ? { onMouseUp: handleMaskMouseUp } : {}

    return (
        <Mask
            style={{ pointerEvents: 'auto' }}
            onMouseDown={handleMaskMouseDown}
            {...closeProps}
        >
            <ModalContainer
                className="my-modal"
                data-name={`modal-${title}`}
                ref={ref}
                width={width}
                $isMobile={isMobile}
                $isMaximized={maximized || innerMaximized}
                onClick={e => {
                    e.stopPropagation()
                }}
            >
                {showConfirmModal && (
                    <ConfirmModal
                        title={'Are you sure you want to close this modal?'}
                        description={
                            confirmOnExit === 'important'
                                ? 'If you close this modal, all the made changes will be lost.'
                                : ''
                        }
                        icon={ICONS_NAME.modalAlert}
                        decline={{
                            label: 'Back',
                            onClick: toggleConfirmModal,
                        }}
                        confirm={{
                            variant: 'red',
                            inverted: true,
                            label: 'Close Modal',
                            onClick: toggle,
                        }}
                    />
                )}

                <ModalHeader
                    title={title}
                    enableMaximize={enableMaximize}
                    isMaximized={maximized || innerMaximized}
                    toggleMaximize={toggleMaximize}
                    toggleModal={handleClose}
                />

                <Scroller $borderWidth="0" $flexGrow={1}>
                    {children}
                </Scroller>
            </ModalContainer>
        </Mask>
    )
}

export { Modal }
